/**
 * Static teal "Your VoteMate Ballot Preview" grabber that lives inside
 * the chat sticky-bottom container, right above the chat input. Used
 * only in the active-chat state.
 *
 * Why this exists as a separate component from ``BallotSheet``: the
 * real sheet uses ``position: fixed`` so it's outside the chat scroll
 * container's flow — which means the browser's compositor doesn't
 * rubber-band it with the chat input during overscroll. By rendering
 * this grabber INSIDE the chat scroll container (as a flex child of
 * the same sticky-bottom container the input lives in), the browser
 * moves them together during overscroll for free, cross-browser.
 *
 * Behavior:
 *   - Visible when the sheet is at the closed resting position
 *     (``heightFrac === 0``).
 *   - Tap → open the real sheet to mid.
 *   - Drag UP → take over the gesture: ``preventDefault`` to suppress
 *     the chat's native scroll/rubber-band, then drive the sheet's
 *     ``heightFrac`` 1:1 with the finger. As soon as ``heightFrac > 0``
 *     the real ``BallotSheet`` fades in (and this grabber fades out).
 *   - Drag DOWN → leave the native chat scroll alone; this is just a
 *     chat overscroll gesture that happens to start on the grabber.
 *
 * Visuals (teal background, grabber pill, "Your VoteMate Ballot
 * Preview" label) intentionally mirror the real sheet's header so the
 * crossfade between grabber and sheet at the start of a drag looks
 * like a single element rather than two.
 */
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Box, Typography } from '@mui/material';

import BallotStatusButton from '@/components/ballot/BallotStatusButton';
import useAnalytics from '@/hooks/useAnalytics';
import useGuestAddress from '@/hooks/useGuestAddress';
import { useBallotSheet, useBallotSheetLive } from '@/providers/BallotSheetProvider';
import { primitiveColors, semanticColors, BORDER_RADIUS_LG } from '@/theme/tokens';
import BallotIcon from '@/ui-library/BallotIcon';

// Matches ``BallotSheet.HEADER_HEIGHT_PX`` so the in-chat grabber
// and the real sheet's header crossfade as the same visual element
// on drag. Sized to fit the title + locality stack (~32px content)
// with breathing room for the pill and vertical padding.
const HEADER_HEIGHT_PX = 56;
// Min vertical movement (px) before we commit a touch on the grabber
// to a sheet-drag gesture (instead of letting it fall through to
// native chat scroll). Matches the drag threshold in BallotSheet.
const DRAG_THRESHOLD_PX = 10;
// Direction filter: the drag must be at least this much more vertical
// than horizontal to commit, so horizontal swipes don't open the
// sheet.
const VERTICAL_DOMINANCE_RATIO = 1.5;

const BallotGrabber: React.FC = () => {
  const { t } = useTranslation('common');
  const {
    detent,
    ballotMode,
    locality,
    viewportHeightPx,
    headerOffsetPx,
    chatInputRect,
    updateDragFrac,
    settleSheet,
    open,
    isStatusPopoverActive,
  } = useBallotSheet();
  const { heightFrac } = useBallotSheetLive();
  const { track } = useAnalytics();
  const { address: guestAddress } = useGuestAddress();
  // Show the status control for a real ballot: personal (signed-in) or a guest
  // with a saved address. Hidden only for the pure national snapshot.
  const showStatus = ballotMode === 'personal' || Boolean(guestAddress?.formatted_address);

  const grabberRef = useRef<HTMLDivElement>(null);
  const isStatusPopoverActiveRef = useRef(isStatusPopoverActive);
  // Captured at touchstart: was the status popover up when this tap began? Its
  // dismiss tap must not open the sheet even on Safari's delayed synthetic click.
  const headerOpenSuppressedRef = useRef(false);
  useEffect(() => {
    isStatusPopoverActiveRef.current = isStatusPopoverActive;
  }, [isStatusPopoverActive]);

  // Travel distance the finger must cover for the sheet to go from
  // closed to full. Matches BallotSheet's drag math so a 1px finger
  // move corresponds to the same heightFrac delta in both places.
  const closedTopY = chatInputRect
    ? chatInputRect.top - HEADER_HEIGHT_PX
    : viewportHeightPx - HEADER_HEIGHT_PX;
  const travelPxRef = useRef(0);
  useEffect(() => {
    travelPxRef.current = Math.max(0, closedTopY - headerOffsetPx);
  }, [closedTopY, headerOffsetPx]);

  // Touch listener — drag up to open. The listener is native (not
  // React synthetic) so we can register ``touchmove`` with
  // ``{passive: false}`` and ``preventDefault`` once we've committed
  // to a drag, which is what suppresses the chat container's native
  // scroll. DOWN-direction movement is intentionally not committed,
  // so a chat overscroll gesture that incidentally starts on the
  // grabber still rubber-bands the chat naturally.
  useEffect(() => {
    const el = grabberRef.current;
    if (!el) return undefined;

    let startX = 0;
    let startY = 0;
    let lastY = 0;
    let lastTime = 0;
    let velocity = 0;
    let mode: 'none' | 'pending' | 'dragging' = 'none';

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        mode = 'none';
        return;
      }
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      lastY = t.clientY;
      lastTime = performance.now();
      velocity = 0;
      mode = 'pending';
      headerOpenSuppressedRef.current = isStatusPopoverActiveRef.current();
    };

    const onTouchMove = (e: TouchEvent) => {
      if (mode === 'none' || e.touches.length !== 1) return;
      const t = e.touches[0];
      const dy = t.clientY - startY; // +dy = finger moved DOWN
      const dx = t.clientX - startX;

      if (mode === 'pending') {
        const absDy = Math.abs(dy);
        const absDx = Math.abs(dx);
        // Only commit on a clearly-upward gesture (negative dy past
        // threshold) so chat overscroll gestures (downward pulls)
        // still hit the native scroll path.
        if (dy < 0 && absDy >= DRAG_THRESHOLD_PX && absDy > absDx * VERTICAL_DOMINANCE_RATIO) {
          mode = 'dragging';
        }
      }

      if (mode === 'dragging') {
        e.preventDefault();
        const travel = travelPxRef.current;
        if (travel > 0) {
          // -dy because finger up = +heightFrac (sheet grows).
          const deltaFrac = -dy / travel;
          updateDragFrac(Math.max(0, deltaFrac));
        }
        const now = performance.now();
        const dt = now - lastTime;
        if (dt > 0) velocity = (lastY - t.clientY) / dt;
        lastY = t.clientY;
        lastTime = now;
      }
    };

    const onTouchEnd = () => {
      if (mode === 'dragging') {
        const travel = travelPxRef.current;
        const dragDy = lastY - startY;
        const currentFrac = travel > 0 ? Math.max(0, Math.min(1, -dragDy / travel)) : 0;
        // Starting detent is always 'closed' here because the grabber
        // is only visible at the closed resting state.
        settleSheet(currentFrac, velocity, 'closed');
      }
      mode = 'none';
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [updateDragFrac, settleSheet]);

  const handleClick = useCallback(() => {
    if (detent !== 'closed') return;
    // A tap dismissing the status popover can hit the grabber — don't open.
    if (headerOpenSuppressedRef.current || isStatusPopoverActive()) {
      headerOpenSuppressedRef.current = false;
      return;
    }
    track('Ballot Sheet Opened', { trigger: 'header' });
    open();
  }, [detent, open, track, isStatusPopoverActive]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (detent !== 'closed') return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        track('Ballot Sheet Opened', { trigger: 'header' });
        open();
      }
    },
    [detent, open, track]
  );

  // Crossfades with the real BallotSheet's header on drag commit. The
  // grabber stays in the DOM the whole time so the in-progress touch
  // sequence keeps firing on it — we just fade it visually and turn
  // off pointer-events once the real sheet has taken over.
  const isAtClosedRest = heightFrac === 0;

  return (
    <Box
      ref={grabberRef}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={t('ballotPreviewLabel')}
      sx={{
        flexShrink: 0,
        height: HEADER_HEIGHT_PX,
        position: 'relative',
        // Mobile-only. On md+ the ballot is opened from the
        // right-edge BallotMenu strip, not from a teal band above
        // the chat input — this grabber doesn't apply.
        display: { xs: 'flex', md: 'none' },
        flexDirection: 'column',
        alignItems: 'center',
        // Pill anchored to the top of the band; the icon + label
        // wrapper below it expands to fill the remaining height and
        // centers the content vertically within that space.
        justifyContent: 'flex-start',
        paddingTop: '6px',
        gap: '4px',
        backgroundColor: semanticColors.ballotElectionCard,
        // Same radius as the composer it docks onto, so band + composer read
        // as one floating card.
        borderTopLeftRadius: BORDER_RADIUS_LG,
        borderTopRightRadius: BORDER_RADIUS_LG,
        // Cancel the inner flex column's ``gap`` below this child so
        // the grabber sits flush on top of the chat input.
        mb: -2,
        // Allow native vertical pan (chat overscroll) when the user's
        // gesture is downward. Our touch listener intercepts upward
        // gestures via ``preventDefault`` once committed to drag.
        touchAction: 'pan-y',
        cursor: 'pointer',
        opacity: isAtClosedRest ? 1 : 0,
        pointerEvents: isAtClosedRest ? 'auto' : 'none',
        transition: 'opacity 100ms ease',
        '&:focus-visible': {
          outline: `2px solid ${primitiveColors.white}`,
          outlineOffset: '-3px',
        },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          // Pill dimensions match the mobile app's
          // ``BallotBanner`` grabber (32 × 4, 2px radius, 40% white).
          width: 32,
          height: 4,
          borderRadius: '2px',
          backgroundColor: 'rgba(255, 255, 255, 0.4)',
        }}
      />
      {/* Ballot icon + label — matches the BallotSheet's full-sheet
          header (and the mobile app's ``BallotBanner``) so the two
          crossfade as the same visual element on drag. ``flex: 1``
          fills the remaining vertical space below the pill;
          ``alignItems: center`` then vertically centers the icon +
          label within that space, while the pill stays pinned at the
          top. */}
      <Box
        sx={{
          flex: 1,
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '2px',
          minWidth: 0,
          // Narrow screens: reserve full-width room for the right-pinned status
          // control so a long translated title stays centered and clears it.
          '@media (max-width:420px)': { paddingLeft: '52px', paddingRight: '52px' },
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, maxWidth: '100%' }}
        >
          <BallotIcon size={16} color={primitiveColors.white} />
          <Typography
            noWrap
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: 1.2,
              color: primitiveColors.white,
              userSelect: 'none',
              '@media (max-width:420px)': { fontSize: '12px' },
            }}
          >
            {t('ballotPreviewLabel')}
          </Typography>
        </Box>
        {locality && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <LocationOnIcon
              sx={{ fontSize: 13, color: primitiveColors.white }}
              aria-hidden="true"
            />
            <Typography
              sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 500,
                fontSize: '11px',
                lineHeight: 1.2,
                color: primitiveColors.white,
                userSelect: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {locality}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Ballot status: condensed dot + chevron pinned right so it's reachable
          while the sheet is collapsed. Its tap opens the status popover without
          opening the sheet (BallotStatusButton stops propagation). */}
      {showStatus && (
        <Box
          sx={{
            position: 'absolute',
            right: 10,
            // Center against the title/locality block (below the grabber pill),
            // not the whole band, so the dot lines up with the text.
            top: '14px',
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <BallotStatusButton condensed />
        </Box>
      )}
    </Box>
  );
};

export default BallotGrabber;
